import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { map, Observable } from 'rxjs';
import { environment } from '@partneradmin/env';
import { UserState } from '../state/user.state';

/**
 * A Tenapod `get-secret-expiration-date` végpont válasz alakja. A backend
 * a már titkosított lejárati időbélyeget adja vissza, amit változtatás
 * nélkül kell továbbküldenünk az `externalRedirectAuth` query stringben
 * `expiration_date` néven.
 */
interface TenapodSecretExpirationDateResponse {
  expirationDate: string;
}

@Injectable({ providedIn: 'root' })
export class TenapodRedirectService {
  constructor(private http: HttpClient, private store: Store) {}

  /**
   * Lekéri a backend-től a Tenapod átirányításhoz szükséges
   * `expirationDate` titkos kulcsot, majd visszaadja a teljes
   * `externalRedirectAuth` URL-t. A hívó dolga, hogy navigáljon vele
   * (pl. `window.location.href = url`) és/vagy hibát kezeljen.
   *
   * A query stringbe három paraméter kerül:
   *  - `email`: a state-ben lévő `UserState.encryptedEmail` (üres string,
   *    ha még nincs betöltve a state — ekkor a Tenapod oldal hibázni
   *    fog, ami helyes viselkedés).
   *  - `path`: a Tenapod oldali cél route a sikeres auth után.
   *  - `expiration_date`: a backend által visszaadott, titkosított
   *    lejárati időbélyeg, változatlanul továbbítva.
   *
   * @param path Tenapod oldali cél route az auth után.
   * @returns Observable, ami a teljes URL stringgel emit-el, vagy a HTTP
   *   hívás hibájával hibázik.
   */
  buildRedirectUrl(path: string): Observable<string> {
    return this.http
      .get<TenapodSecretExpirationDateResponse>(
        `${environment.backendUrl}/partner/tenapod/get-secret-expiration-date`
      )
      .pipe(
        map(({ expirationDate }) => {
          const email = this.store.selectSnapshot(UserState.encryptedEmail) ?? '';
          const params = new URLSearchParams({
            email,
            path,
            expiration_date: expirationDate,
          });
          return `${environment.tenapodPartnerUrl}/externalRedirectAuth?${params.toString()}`;
        })
      );
  }
}
